import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import { useChatWorkspaceAccess } from "@/lib/hooks/conversations/use-chat-workspace-access";
import {
  MENTOR_WORKSPACES,
  readStoredMentorWorkspace,
  resolveMentorWorkspace,
  storeMentorWorkspace,
  type MentorWorkspaceId,
  type MentorWorkspaceOption,
} from "./workspace";

const CONVERSATIONS_ROUTE = "/conversations/" as const;

interface WorkspaceSearch {
  segment?: string;
}

export interface UseMentorWorkspaceResult {
  workspace: MentorWorkspaceId;
  workspaces: MentorWorkspaceOption[];
  setWorkspace: (workspace: MentorWorkspaceId) => void;
}

export function useMentorWorkspace(): UseMentorWorkspaceResult {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as WorkspaceSearch;
  const [storedWorkspace] = useState<MentorWorkspaceId | null>(() =>
    readStoredMentorWorkspace(),
  );
  const { canSeeTeens, canSeeSupport } = useChatWorkspaceAccess();

  const workspaces = useMemo(
    () =>
      MENTOR_WORKSPACES.filter((option) => {
        if (option.id === "teens") return canSeeTeens;
        return canSeeSupport;
      }),
    [canSeeTeens, canSeeSupport],
  );

  const resolvedWorkspace = resolveMentorWorkspace(
    search.segment,
    storedWorkspace,
  );
  const workspace = workspaces.some((option) => option.id === resolvedWorkspace)
    ? resolvedWorkspace
    : (workspaces[0]?.id ?? resolvedWorkspace);

  const setWorkspace = useCallback(
    (next: MentorWorkspaceId) => {
      storeMentorWorkspace(next);
      void navigate({
        from: CONVERSATIONS_ROUTE,
        search: (prev) => ({
          ...prev,
          segment: next,
          channel: undefined,
          internalChannelId: undefined,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  return { workspace, workspaces, setWorkspace };
}
